import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Heart,
  LockKeyhole,
  Pause,
  Play,
  Search,
  Share2,
  Sparkles,
  Workflow,
  X,
} from 'lucide-react';
import { Background, Controls, Handle, MiniMap, Position, ReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import BorderGlow from '../components/BorderGlow';
import PageTitleBlock from '../components/PageTitleBlock';
import { PROTOTYPE_CASES, findPrototypeCase } from './learningPrototypeData';

const PROTOTYPE_ROOT = '/prototype/vnext/learn';

function PrototypeNotice() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-800 dark:border-amber-300/15 dark:bg-amber-300/[0.08] dark:text-amber-100/80">
      <Sparkles size={13} />演示内容 · 不会创建作品或消耗点数
    </div>
  );
}

function PreviewVideo({ media, label, className = '', autoplay = false, controls = false, hoverPreview = false, videoRef, onPlay, onPause, onEnded }) {
  const startPreview = event => {
    if (!hoverPreview || autoplay) return;
    const playResult = event.currentTarget.play();
    playResult?.catch?.(() => {});
  };
  const stopPreview = event => {
    if (!hoverPreview || autoplay) return;
    event.currentTarget.pause();
    event.currentTarget.currentTime = 0;
  };

  return (
    <video
      ref={videoRef}
      className={className}
      src={media.video}
      poster={media.cover}
      muted
      loop={!controls}
      autoPlay={autoplay}
      controls={controls}
      playsInline
      preload={autoplay ? 'metadata' : 'none'}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      onPlay={onPlay}
      onPause={onPause}
      onEnded={onEnded}
      aria-label={label}
    />
  );
}

function CaseCard({ item, onOpen }) {
  return (
    <article className="group min-w-0">
      <button type="button" aria-label={`打开案例：${item.title}`} onClick={() => onOpen(item.id)} className="block w-full text-left">
        <div className="relative aspect-[4/3] overflow-hidden rounded-[20px] bg-stone-900 shadow-[0_24px_52px_-42px_rgba(40,31,44,0.7)] ring-1 ring-black/[0.06] transition duration-300 group-hover:shadow-[0_30px_62px_-42px_rgba(40,31,44,0.78)] dark:ring-white/[0.08]">
          <img src={item.cover} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/78" />
          <div className="absolute left-3 top-3 flex items-center gap-2">
            <span className="rounded-full border border-white/15 bg-black/35 px-2.5 py-1 text-[9px] font-medium text-white backdrop-blur-md">{item.category}</span>
            {Number(item.episodeCount) > 0 && (
              <span className="rounded-full border border-white/15 bg-black/35 px-2.5 py-1 text-[9px] text-white/75 backdrop-blur-md">{item.episodeCount} 集</span>
            )}
          </div>
          <span className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full border border-white/20 bg-black/25 text-white opacity-0 backdrop-blur transition group-hover:opacity-100"><Play size={13} fill="currentColor" /></span>
          <div className="absolute inset-x-0 bottom-0 p-4 text-white">
            <h2 className="text-[17px] font-semibold tracking-[-0.025em]">{item.title}</h2>
            <p className="mt-1 truncate text-[10px] text-white/55">{item.creator} · {item.workflowPublic ? '制作过程公开' : '仅展示作品'}</p>
          </div>
        </div>
      </button>
    </article>
  );
}

function LearningIndex() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const learningFilters = useMemo(() => [...new Set(PROTOTYPE_CASES.flatMap(item => item.learningTags || []))], []);
  const visibleCases = useMemo(() => PROTOTYPE_CASES.filter(item => {
    const keyword = query.trim().toLocaleLowerCase('zh-CN');
    const matchesFilter = !activeFilter || item.learningTags?.includes(activeFilter);
    const matchesQuery = !keyword || [item.title, item.summary, item.category, item.tags.join(' '), item.learningTags?.join(' ')]
      .join(' ')
      .toLocaleLowerCase('zh-CN')
      .includes(keyword);
    return matchesFilter && matchesQuery;
  }), [activeFilter, query]);
  const openCase = caseId => navigate(`${PROTOTYPE_ROOT}/cases/${caseId}`);

  return (
    <div className="vnext-page-surface">
      <div className="vnext-page-frame">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <PageTitleBlock
            title="学习案例"
          />
          <BorderGlow
            edgeSensitivity={30}
            glowColor="266 42 66"
            backgroundColor="var(--vnext-composer)"
            borderRadius={14}
            glowRadius={28}
            glowIntensity={0.78}
            coneSpread={25}
            animated={false}
            colors={['#9784c2', '#b88eb9', '#7f99c5']}
            fillOpacity={0.14}
            className="w-full md:w-[340px]"
          >
            <label className="flex h-11 w-full items-center gap-2 rounded-[13px] bg-white/72 px-3.5 dark:bg-white/[0.045]">
              <Search size={15} className="text-stone-400" />
              <input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索案例、题材或方法" aria-label="搜索原型案例" className="min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-stone-400" />
              {query && <button type="button" onClick={() => setQuery('')} aria-label="清空搜索"><X size={14} /></button>}
            </label>
          </BorderGlow>
        </div>
        <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="radiogroup" aria-label="案例分类">
          {['', ...learningFilters].map(filter => {
            const active = filter === activeFilter;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={active}
                key={filter || 'all'}
                onClick={() => setActiveFilter(filter)}
                className={`h-9 shrink-0 rounded-full border px-4 text-[12px] font-medium transition ${active
                  ? 'border-[#78668d]/18 bg-[#352f3b] text-white shadow-sm dark:border-transparent dark:bg-[#c1afe3] dark:text-[#241e29]'
                  : 'border-black/[0.07] bg-white/52 text-stone-500 hover:border-[#806b9a]/20 hover:bg-white hover:text-stone-850 dark:border-white/[0.08] dark:bg-white/[0.035] dark:text-white/42 dark:hover:bg-white/[0.07] dark:hover:text-white/75'}`}
              >
                {filter || '全部'}
              </button>
            );
          })}
        </div>
        <section className="mt-6 border-t border-black/[0.06] pt-7 dark:border-white/[0.07]" aria-label="案例列表">
          {visibleCases.length ? <div className="grid gap-[14px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{visibleCases.map(item => <CaseCard item={item} onOpen={openCase} key={item.id} />)}</div> : <div className="grid min-h-64 place-items-center rounded-[24px] border border-dashed border-black/10 text-center text-[12px] text-stone-400 dark:border-white/10">没有找到匹配案例</div>}
        </section>
      </div>
    </div>
  );
}

function CaseDetail() {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const item = findPrototypeCase(caseId);
  const [selectedEpisode, setSelectedEpisode] = useState(0);
  const [favorite, setFavorite] = useState(false);
  const [shared, setShared] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const episode = item.episodes[selectedEpisode];

  useEffect(() => {
    const scrollContainer = document.querySelector('main');
    if (typeof scrollContainer?.scrollTo === 'function') scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
  }, [caseId]);

  useEffect(() => {
    setIsPlaying(false);
  }, [episode.number]);

  const togglePlayback = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      const playResult = videoRef.current.play();
      playResult?.catch?.(() => {});
    }
    else videoRef.current.pause();
  };

  return (
    <div className="min-h-full bg-[#09090a] text-white">
      <div className="mx-auto max-w-[1480px] px-4 pb-14 pt-4 sm:px-7">
        <div className="mb-4 flex items-center gap-3">
          <button type="button" onClick={() => navigate(PROTOTYPE_ROOT)} className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 text-[11px] text-white/70 hover:bg-white/[0.09]"><ArrowLeft size={14} />返回</button>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-[#b99edb] to-[#625274] text-[10px] font-semibold">XY</span>
          <div className="min-w-0"><div className="truncate text-[11px] text-white/55">{item.creator}</div><div className="truncate text-[13px] font-medium">{item.title}</div></div>
          <div className="ml-auto hidden sm:block"><PrototypeNotice /></div>
        </div>
        <section className="relative overflow-hidden rounded-[18px] border border-white/[0.08] bg-black shadow-2xl">
          <PreviewVideo
            key={`${item.id}-${episode.number}`}
            videoRef={videoRef}
            media={episode}
            label={`${item.title}第${episode.number}集预览视频`}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            className="aspect-video max-h-[76vh] w-full bg-black object-contain"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/10" />
          <div className="absolute inset-x-0 bottom-8 flex items-center justify-center gap-2.5 px-4 sm:bottom-10">
            <button type="button" onClick={togglePlayback} aria-label={isPlaying ? `暂停第 ${episode.number} 集` : `播放第 ${episode.number} 集`} className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-[#1d1920] shadow-xl transition hover:scale-105"><span className="sr-only">{isPlaying ? '暂停' : '播放'}</span>{isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}</button>
            {item.workflowPublic ? <button type="button" onClick={() => navigate(`${PROTOTYPE_ROOT}/cases/${item.id}/workflow`)} className="inline-flex h-12 items-center gap-2 rounded-full bg-black/55 px-5 text-[11px] font-medium text-white shadow-xl backdrop-blur-md transition hover:bg-black/70"><Workflow size={15} />查看制作过程</button> : <span aria-label="制作过程未公开" className="inline-flex h-12 items-center gap-2 rounded-full bg-black/45 px-5 text-[11px] text-white/45 backdrop-blur-md"><LockKeyhole size={14} />制作过程未公开</span>}
            <button type="button" aria-pressed={favorite} onClick={() => setFavorite(value => !value)} aria-label={favorite ? '取消收藏' : '收藏'} className={`grid h-12 w-12 shrink-0 place-items-center rounded-full shadow-xl backdrop-blur-md transition hover:scale-105 ${favorite ? 'bg-white text-rose-500' : 'bg-black/45 text-white'}`}><Heart size={18} fill={favorite ? 'currentColor' : 'none'} /></button>
            <button type="button" onClick={() => setShared(true)} aria-label={shared ? '分享链接已准备' : '分享'} className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-black/45 text-white shadow-xl backdrop-blur-md transition hover:scale-105"><Share2 size={17} /></button>
          </div>
        </section>

        <section className="mt-3 overflow-x-auto pb-2" aria-label="选集">
          <div className="flex min-w-max gap-3" role="tablist" aria-label={`${item.title}选集`}>
            {item.episodes.map((entry, index) => (
              <button
                type="button"
                role="tab"
                aria-selected={selectedEpisode === index}
                aria-label={`播放第 ${entry.number} 集：${entry.title}`}
                key={entry.number}
                onClick={() => setSelectedEpisode(index)}
                className={`group relative w-[112px] shrink-0 overflow-hidden rounded-[12px] text-left transition sm:w-[132px] ${selectedEpisode === index ? 'ring-2 ring-white ring-offset-2 ring-offset-[#09090a]' : 'opacity-60 hover:opacity-100'}`}
              >
                <div className="aspect-square overflow-hidden bg-white/5"><img src={entry.cover} alt="" className="h-full w-full object-cover transition group-hover:scale-[1.03]" /></div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-3 pb-2.5 pt-8">
                  <div className="text-[10px] font-semibold">第 {entry.number} 集</div>
                  <div className="mt-0.5 truncate text-[8px] text-white/55">{entry.title}</div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <div className="mt-5 border-t border-white/[0.07] pt-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[9px] text-white/38"><span>{item.category}</span><span>·</span><span>第 {episode.number} 集 / 共 5 集</span></div>
            <h1 className="mt-2 text-[24px] font-semibold tracking-[-0.035em] md:text-[30px]">第 {episode.number} 集 · {episode.title}</h1>
            <p className="mt-2 max-w-3xl text-[11px] leading-6 text-white/42">{item.summary}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkflowNode({ data }) {
  return (
    <div className={`w-[250px] overflow-hidden rounded-[18px] border bg-[#211d25] shadow-[0_18px_45px_-24px_rgba(0,0,0,0.75)] ${data.focus ? 'border-[#c8b1e7]/60 ring-4 ring-[#a98bd0]/10' : 'border-white/10'}`}>
      <Handle type="target" position={Position.Left} className="!h-2.5 !w-2.5 !border-2 !border-[#211d25] !bg-[#af95cc]" />
      {data.image && <img src={data.image} alt="" className="h-28 w-full object-cover" />}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2"><span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#bca5d8]">{data.type}</span>{data.public && <span className="rounded-full bg-emerald-400/10 px-2 py-0.5 text-[8px] text-emerald-200/70">公开</span>}</div>
        <div className="mt-2 text-[13px] font-semibold text-white/88">{data.title}</div>
        <p className="mt-2 text-[9px] leading-4 text-white/38">{data.description}</p>
      </div>
      <Handle type="source" position={Position.Right} className="!h-2.5 !w-2.5 !border-2 !border-[#211d25] !bg-[#af95cc]" />
    </div>
  );
}

const nodeTypes = { prototype: WorkflowNode };

function buildWorkflow(item, guide) {
  const nodes = [
    { id: 'brief', type: 'prototype', position: { x: 0, y: 180 }, data: { type: '创意简报', title: '退婚书成为第一集钩子', description: '目标、受众、时长和不可改变的故事条件。', public: true, focus: guide === 1 } },
    { id: 'script', type: 'prototype', position: { x: 330, y: 180 }, data: { type: '剧本', title: '第一集候选稿', description: '接受后的正文版本；聊天草稿和未接受候选不公开。', public: true, focus: guide === 2 } },
    { id: 'character', type: 'prototype', position: { x: 660, y: 10 }, data: { type: '角色资产', title: '苏照影标准形象', description: '只展示已确认、已授权的角色标准图。', image: '/images/vnext/assets/su-zhaoying.jpg', public: true, focus: guide === 3 } },
    { id: 'scene', type: 'prototype', position: { x: 660, y: 330 }, data: { type: '场景资产', title: '雪落密室', description: '场景参考、色调和空间锚点。', image: '/images/vnext/assets/xueluo-secret-room.jpg', public: true, focus: guide === 3 } },
    { id: 'storyboard', type: 'prototype', position: { x: 1020, y: 180 }, data: { type: '分镜组', title: '12 个确认分镜', description: '展示镜头目的和顺序，不包含失败任务回执。', image: item.cover, public: true, focus: guide === 4 } },
    { id: 'delivery', type: 'prototype', position: { x: 1380, y: 180 }, data: { type: '交付视频', title: item.title, description: '通过 QC 的成片版本。复制后不会自动运行或扣点。', image: item.cover, public: true, focus: guide === 5 } },
  ];
  const edges = [
    ['brief', 'script'], ['script', 'character'], ['script', 'scene'], ['character', 'storyboard'], ['scene', 'storyboard'], ['storyboard', 'delivery'],
  ].map(([source, target], index) => ({ id: `edge-${index}`, source, target, animated: index === Math.min(guide, 5), style: { stroke: '#9b82b8', strokeWidth: 1.5 } }));
  return { nodes, edges };
}

function WorkflowPreview() {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const item = findPrototypeCase(caseId);
  const [guide, setGuide] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const graph = useMemo(() => buildWorkflow(item, guide), [guide, item]);

  if (!item.workflowPublic) return <CaseDetail />;

  return (
    <div className="relative h-[calc(100vh-1px)] min-h-[720px] overflow-hidden bg-[#121014] text-white">
      <header className="absolute inset-x-0 top-0 z-20 flex h-[68px] items-center gap-4 border-b border-white/[0.08] bg-[#1c191f]/95 px-5 backdrop-blur-xl">
        <button type="button" onClick={() => navigate(`${PROTOTYPE_ROOT}/cases/${item.id}`)} className="grid h-9 w-9 place-items-center rounded-full text-white/55 hover:bg-white/[0.07] hover:text-white"><ArrowLeft size={16} /></button>
        <div className="min-w-0"><div className="flex items-center gap-2"><h1 className="truncate text-[15px] font-semibold">{item.title}</h1><span className="rounded-full border border-white/10 bg-white/[0.05] px-2 py-0.5 text-[9px] text-white/38">只读预览</span></div><div className="mt-0.5 text-[9px] text-white/30">公开版本 v1 · 原作者后续修改不会改变此快照</div></div>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setGuide(current => current >= 5 ? 0 : current + 1)} className="hidden h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 text-[10px] text-white/65 hover:bg-white/[0.09] sm:inline-flex"><Sparkles size={13} />{guide ? `关键步骤 ${guide}/5` : '解读创作思路'}</button>
          <button type="button" onClick={() => setConfirming(true)} className="inline-flex h-9 items-center gap-2 rounded-full bg-white px-4 text-[10px] font-semibold text-[#28222f] hover:bg-[#eee7f7]"><Copy size={13} />体验演示画布</button>
        </div>
      </header>

      <div className="absolute inset-0 pt-[68px]">
        <ReactFlow
          nodes={graph.nodes}
          edges={graph.edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.18 }}
          minZoom={0.25}
          maxZoom={1.5}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          deleteKeyCode={null}
          proOptions={{ hideAttribution: true }}
          colorMode="dark"
        >
          <Background color="#403848" gap={28} size={1} />
          <Controls showInteractive={false} className="!border-white/10 !bg-[#28232d] !shadow-xl [&_button]:!border-white/10 [&_button]:!bg-[#28232d] [&_button]:!fill-white/60" />
          <MiniMap pannable zoomable nodeColor="#856e9d" maskColor="rgba(14,12,16,0.72)" className="!border !border-white/10 !bg-[#211d25]" />
        </ReactFlow>
      </div>

      {guide > 0 && <aside className="absolute bottom-6 left-1/2 z-20 w-[min(520px,calc(100%-32px))] -translate-x-1/2 rounded-[18px] border border-[#b497d3]/20 bg-[#211c26]/95 p-4 shadow-2xl backdrop-blur-xl"><div className="flex items-start gap-3"><span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#c5b1df] text-[10px] font-bold text-[#28212f]">{guide}</span><div><div className="text-[11px] font-semibold">{['', '先固定创作目标', '只公开已接受的剧本', '角色和场景成为一致性锚点', '分镜确认后再开始花点生产', '在演示画布中体验节点操作'][guide]}</div><p className="mt-1 text-[9px] leading-4 text-white/40">点击“关键步骤”继续，画布会依次高亮这条案例真正值得学习的节点。</p></div></div></aside>}

      {confirming && <div className="absolute inset-0 z-40 grid place-items-center bg-black/60 p-4 backdrop-blur-sm" role="presentation"><section role="dialog" aria-modal="true" aria-labelledby="clone-title" className="w-full max-w-md rounded-[24px] border border-white/10 bg-[#242027] p-6 shadow-2xl"><div className="flex items-start justify-between gap-4"><div><div className="grid h-10 w-10 place-items-center rounded-full bg-[#c6b2e3]/15 text-[#d2c2e7]"><Copy size={17} /></div><h2 id="clone-title" className="mt-4 text-[18px] font-semibold">体验演示画布</h2><p className="mt-2 text-[11px] leading-6 text-white/43">将打开一个示例画布，供你浏览节点和体验操作；不会创建个人副本、运行模型或扣除点数。</p></div><button type="button" onClick={() => setConfirming(false)} aria-label="关闭体验说明" className="grid h-8 w-8 place-items-center rounded-full text-white/35 hover:bg-white/[0.07]"><X size={15} /></button></div><div className="mt-5 rounded-[16px] border border-white/[0.07] bg-white/[0.035] p-4 text-[10px] leading-5 text-white/45"><div className="flex items-center justify-between"><span>公开节点</span><strong className="text-white/70">6 个</strong></div><div className="mt-2 flex items-center justify-between"><span>演示内容保存</span><strong className="text-white/70">不保存</strong></div><div className="mt-2 flex items-center justify-between"><span>本次操作预计点数</span><strong className="text-emerald-300/80">0 点</strong></div></div><div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setConfirming(false)} className="h-10 rounded-full border border-white/10 px-4 text-[10px] text-white/55">取消</button><button type="button" onClick={() => { setConfirming(false); navigate('/prototype/vnext/canvas/preview-canvas'); }} className="h-10 rounded-full bg-white px-5 text-[10px] font-semibold text-[#28222f]">开始体验</button></div></section></div>}
    </div>
  );
}

export default function LearningPrototypePage({ view = 'index' }) {
  if (view === 'detail') return <CaseDetail />;
  if (view === 'workflow') return <WorkflowPreview />;
  return <LearningIndex />;
}
